import React, { useEffect, useState } from "react";
import CommentIcon from "@mui/icons-material/Comment";
import { Order } from "../../../api/order";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSButton from "../../system-design/DSButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSTextField from "../../system-design/DSTextField";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import { sectionCardSx } from "../../system-design/sectionCardSx";
import useUpdateOrderCommentMutation from "../hooks/useUpdateOrderCommentMutation";

const MAX_COMMENT_LENGTH = 5000;

interface OrderCommentSectionProps {
  order: Order;
}

const OrderCommentSection = ({ order }: OrderCommentSectionProps) => {
  const savedComment = order.comment ?? "";
  const [draft, setDraft] = useState(savedComment);
  const { mutate: updateComment, isPending: isSaving } =
    useUpdateOrderCommentMutation(order.id);

  // Pick up the saved value when the modal switches order or the query
  // refetches — the draft is only ours while it differs from what is stored.
  useEffect(() => {
    setDraft(savedComment);
  }, [savedComment, order.id]);

  const isDirty = draft !== savedComment;
  const isTooLong = draft.trim().length > MAX_COMMENT_LENGTH;

  return (
    <DSBox sx={[{ mt: 3, p: 2 }, sectionCardSx]}>
      <DSTypography
        variant="h6"
        sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
      >
        <CommentIcon />
        Comment
      </DSTypography>
      <DSTextField
        fullWidth
        multiline
        minRows={3}
        placeholder="Add a note about this order (internal only)"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        disabled={isSaving}
        error={isTooLong}
        helperText={
          isTooLong
            ? `Comment exceeds ${MAX_COMMENT_LENGTH} characters`
            : undefined
        }
      />
      <DSBox sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
        <DSButton
          size="small"
          onClick={() => updateComment(draft.trim())}
          disabled={!isDirty || isTooLong || isSaving}
        >
          Save Comment
        </DSButton>
        <DSSecondaryButton
          size="small"
          onClick={() => setDraft(savedComment)}
          disabled={!isDirty || isSaving}
        >
          Cancel
        </DSSecondaryButton>
        {isSaving && <DSCircularProgress size={16} />}
      </DSBox>
    </DSBox>
  );
};

export default OrderCommentSection;
